import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Sparkles, CheckCircle2, XCircle, Loader2, Play, Square, FileText, FileQuestion, BookOpen, LayoutList } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { getTeacherSubjects } from './subjectConfig';

const API_URL = '';

const ASSESSMENT_TYPES = [
  { value: 'quiz', label: 'Quiz' },
  { value: 'worksheet', label: 'Worksheet' },
  { value: 'exercise', label: 'Exercise' },
  { value: 'assignment', label: 'Assignment' },
  { value: 'homework', label: 'Homework' },
  { value: 'research', label: 'Research Task' },
  { value: 'presentation', label: 'Presentation Task' },
  { value: 'test', label: 'Test' },
  { value: 'project', label: 'Project' },
];

// Year 12 ICT detailed weekly topics (kept for ICT-specific data)
const YEAR_12_ICT = [
  { id: 'y12-w1', term: 'Term 1', week: 'Week 1', topic: 'Introduction to PC Hardware Components - Major hardware: CPU, RAM, HDD/SSD, Motherboard', unit: 'Unit 1: PC Hardware', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w2', term: 'Term 1', week: 'Week 2', topic: 'PC Ports, Cables and Connectors - USB, HDMI, VGA, Ethernet ports', unit: 'Unit 1: PC Hardware', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w3', term: 'Term 1', week: 'Week 3', topic: 'Hardware Diagnostics - Running diagnostic tests and identifying hardware issues', unit: 'Unit 1: PC Hardware', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w4', term: 'Term 1', week: 'Week 4', topic: 'PC Assembly Safety and Procedures - ESD safety, workspace setup', unit: 'Unit 2: Building a PC', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w5', term: 'Term 1', week: 'Week 5', topic: 'Step-by-Step PC Building - Assembling CPU, RAM, motherboard, storage', unit: 'Unit 2: Building a PC', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w6', term: 'Term 1', week: 'Week 6', topic: 'Power-on Testing and Troubleshooting - POST, BIOS setup', unit: 'Unit 2: Building a PC', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w7', term: 'Term 1', week: 'Week 7', topic: 'Types of Operating Systems - Comparing Windows, Linux, macOS', unit: 'Unit 3: OS Installation', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w8', term: 'Term 1', week: 'Week 8', topic: 'Windows Installation Process - Partitioning, formatting, installing', unit: 'Unit 3: OS Installation', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w9', term: 'Term 1', week: 'Week 9', topic: 'Driver Installation and OS Testing - Installing drivers, testing', unit: 'Unit 3: OS Installation', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w10', term: 'Term 1', week: 'Week 10', topic: 'Term 1 Review - PC Systems comprehensive review', unit: 'Review', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w11', term: 'Term 2', week: 'Week 1', topic: 'Advanced Word Processing - Text vs Word processors, formatting, styles', unit: 'Unit 5: Word Processing', strand: 'Strand 2: Application Software' },
  { id: 'y12-w12', term: 'Term 2', week: 'Week 2', topic: 'Document Layout - Headers, footers, page numbers, tables, images', unit: 'Unit 5: Word Processing', strand: 'Strand 2: Application Software' },
  { id: 'y12-w13', term: 'Term 2', week: 'Week 3', topic: 'Professional Documents - Templates, cover pages, formal documents', unit: 'Unit 5: Word Processing', strand: 'Strand 2: Application Software' },
  { id: 'y12-w14', term: 'Term 2', week: 'Week 4', topic: 'Mail Merge - Creating form letters, mailing labels, data sources', unit: 'Unit 6: Mail Merge', strand: 'Strand 2: Application Software' },
  { id: 'y12-w15', term: 'Term 2', week: 'Week 5', topic: 'Citations and Bibliographies - Academic referencing', unit: 'Unit 6: Citations', strand: 'Strand 2: Application Software' },
  { id: 'y12-w16', term: 'Term 2', week: 'Week 6', topic: 'Spreadsheet Navigation and Data Entry - Cell formatting, data types', unit: 'Unit 7: Spreadsheets', strand: 'Strand 2: Application Software' },
  { id: 'y12-w17', term: 'Term 2', week: 'Week 7', topic: 'Spreadsheet Formulas - SUM, AVERAGE, COUNT, MIN, MAX', unit: 'Unit 7: Spreadsheets', strand: 'Strand 2: Application Software' },
  { id: 'y12-w18', term: 'Term 2', week: 'Week 8', topic: 'Cell Referencing - Relative, absolute, and mixed references', unit: 'Unit 7: Spreadsheets', strand: 'Strand 2: Application Software' },
  { id: 'y12-w19', term: 'Term 2', week: 'Week 9', topic: 'Charts and Graphs - Bar, pie, line charts and data visualization', unit: 'Unit 8: Charts', strand: 'Strand 2: Application Software' },
  { id: 'y12-w20', term: 'Term 2', week: 'Week 10', topic: 'Data Analysis - Sorting, filtering, conditional formatting', unit: 'Unit 8: Data Analysis', strand: 'Strand 2: Application Software' },
  { id: 'y12-w21', term: 'Term 3', week: 'Week 1', topic: 'Database Fundamentals - DBMS and relational database concepts', unit: 'Unit 9: Databases', strand: 'Strand 2: Application Software' },
  { id: 'y12-w22', term: 'Term 3', week: 'Week 2', topic: 'Database Design - Tables, fields, data types, primary keys', unit: 'Unit 9: Databases', strand: 'Strand 2: Application Software' },
  { id: 'y12-w23', term: 'Term 3', week: 'Week 3', topic: 'Creating Databases - Building tables and entering data', unit: 'Unit 9: Databases', strand: 'Strand 2: Application Software' },
  { id: 'y12-w24', term: 'Term 3', week: 'Week 4', topic: 'Database Forms - Creating user-friendly data entry forms', unit: 'Unit 10: Forms & Queries', strand: 'Strand 2: Application Software' },
  { id: 'y12-w25', term: 'Term 3', week: 'Week 5', topic: 'Queries and Reports - Extracting data and generating reports', unit: 'Unit 10: Forms & Queries', strand: 'Strand 2: Application Software' },
  { id: 'y12-w26', term: 'Term 3', week: 'Week 6', topic: 'Digital Image Basics - Types, resolution, file formats', unit: 'Unit 11: Digital Imaging', strand: 'Strand 3: Graphics' },
  { id: 'y12-w27', term: 'Term 3', week: 'Week 7', topic: 'Image Editing - Cropping, resizing, layers, filters', unit: 'Unit 11: Digital Imaging', strand: 'Strand 3: Graphics' },
  { id: 'y12-w28', term: 'Term 3', week: 'Week 8', topic: 'Introduction to Programming - Languages and algorithms', unit: 'Unit 12: Programming', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w29', term: 'Term 3', week: 'Week 9', topic: 'Programming Logic - Variables, data types, input/output, pseudocode', unit: 'Unit 12: Programming', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w30', term: 'Term 3', week: 'Week 10', topic: 'Control Structures - If/else, loops, and program writing', unit: 'Unit 12: Programming', strand: 'Strand 1: PC Systems' },
  { id: 'y12-w31', term: 'Term 4', week: 'Week 1', topic: 'Network Fundamentals - LAN, WAN, MAN, PAN types', unit: 'Unit 13: Networks', strand: 'Strand 4: Communication' },
  { id: 'y12-w32', term: 'Term 4', week: 'Week 2', topic: 'Network Topologies - Star, bus, ring, mesh', unit: 'Unit 13: Networks', strand: 'Strand 4: Communication' },
  { id: 'y12-w33', term: 'Term 4', week: 'Week 3', topic: 'Network Protocols and Devices - TCP/IP, routers, switches', unit: 'Unit 13: Networks', strand: 'Strand 4: Communication' },
  { id: 'y12-w34', term: 'Term 4', week: 'Week 4', topic: 'HTML Fundamentals - Tags, structure, headings, lists', unit: 'Unit 14: Web Pages', strand: 'Strand 4: Communication' },
  { id: 'y12-w35', term: 'Term 4', week: 'Week 5', topic: 'CSS Styling - Colors, fonts, layouts for web pages', unit: 'Unit 14: Web Pages', strand: 'Strand 4: Communication' },
  { id: 'y12-w36', term: 'Term 4', week: 'Week 6', topic: 'Building Web Pages - Multi-page website with links, images', unit: 'Unit 14: Web Pages', strand: 'Strand 4: Communication' },
  { id: 'y12-w37', term: 'Term 4', week: 'Week 7', topic: 'Internet Safety - Phishing, malware, social engineering', unit: 'Unit 15: Security', strand: 'Strand 4: Communication' },
  { id: 'y12-w38', term: 'Term 4', week: 'Week 8', topic: 'Data Privacy and Protection - Passwords, encryption, backup', unit: 'Unit 15: Security', strand: 'Strand 4: Communication' },
  { id: 'y12-w39', term: 'Term 4', week: 'Week 9', topic: 'Safe Online Practices - Digital footprint, cyberbullying', unit: 'Unit 15: Security', strand: 'Strand 4: Communication' },
  { id: 'y12-w40', term: 'Term 4', week: 'Week 10', topic: 'Term 4 Review - Networks and Security review', unit: 'Review', strand: 'Strand 4: Communication' },
];

const YEAR_13_ICT = [
  { id: 'y13-w1', term: 'Term 1', week: 'Week 1', topic: 'Future ICT Trends in Education - Digital technology transforming learning', unit: 'Future ICT Trends', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w2', term: 'Term 1', week: 'Week 2', topic: 'Future ICT Trends in Health - Telemedicine, health informatics', unit: 'Future ICT Trends', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w3', term: 'Term 1', week: 'Week 3', topic: 'Future ICT Trends at Home - Smart homes, IoT devices', unit: 'Future ICT Trends', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w4', term: 'Term 1', week: 'Week 4', topic: 'Preparing Vanuatu for Future ICT - Connectivity, digital literacy', unit: 'Future ICT Trends', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w5', term: 'Term 1', week: 'Week 5', topic: 'Workflows and Information Systems - Workflow processes, IS platforms', unit: 'Management of IS', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w6', term: 'Term 1', week: 'Week 6', topic: 'IS in Education and Government - Public sector applications', unit: 'Management of IS', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w7', term: 'Term 1', week: 'Week 7', topic: 'Information Systems Development - SDLC phases', unit: 'Management of IS', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w8', term: 'Term 1', week: 'Week 8', topic: 'IS Development Process - Requirements, design, implementation', unit: 'Management of IS', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w9', term: 'Term 1', week: 'Week 9', topic: 'Digital Media Devices and Applications - Audio, video, image tools', unit: 'Digital Media', strand: 'Strand 2: Application Software' },
  { id: 'y13-w10', term: 'Term 1', week: 'Week 10', topic: 'Creating Digital Media Content - Audio/video editing, graphic design', unit: 'Digital Media', strand: 'Strand 2: Application Software' },
  { id: 'y13-w11', term: 'Term 2', week: 'Week 1', topic: 'ICT Infrastructure Requirements - Hardware, software for networks', unit: 'ICT Infrastructure', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w12', term: 'Term 2', week: 'Week 2', topic: 'Network Types - LAN, WAN, MAN, WiFi setup', unit: 'ICT Infrastructure', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w13', term: 'Term 2', week: 'Week 3', topic: 'Open Source vs Proprietary Software - Comparing free and commercial', unit: 'ICT Infrastructure', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w14', term: 'Term 2', week: 'Week 4', topic: 'Software Installation and Management - Install, update, manage', unit: 'ICT Infrastructure', strand: 'Strand 1: PC Systems' },
  { id: 'y13-w15', term: 'Term 2', week: 'Week 5', topic: 'Web Design Requirements - Planning, designing websites', unit: 'Database Websites', strand: 'Strand 2: Application Software' },
  { id: 'y13-w16', term: 'Term 2', week: 'Week 6', topic: 'CSS and HTML Integration - Advanced web styling', unit: 'Database Websites', strand: 'Strand 2: Application Software' },
  { id: 'y13-w17', term: 'Term 2', week: 'Week 7', topic: 'Database-Driven Websites - Connecting databases to web pages', unit: 'Database Websites', strand: 'Strand 2: Application Software' },
  { id: 'y13-w18', term: 'Term 2', week: 'Week 8', topic: 'Web Programming Basics - Server-side scripting, form handling', unit: 'Database Websites', strand: 'Strand 2: Application Software' },
  { id: 'y13-w19', term: 'Term 2', week: 'Week 9', topic: 'Website Testing and Validation - Cross-browser, W3C', unit: 'Database Websites', strand: 'Strand 2: Application Software' },
  { id: 'y13-w20', term: 'Term 2', week: 'Week 10', topic: 'Term 2 Review - ICT Infrastructure and Web Development', unit: 'Review', strand: 'Strand 2: Application Software' },
  { id: 'y13-w21', term: 'Term 3', week: 'Week 1', topic: 'Unicode and Character Encoding - ASCII, Unicode, character sets', unit: 'Data Representation', strand: 'Strand 3: Programming' },
  { id: 'y13-w22', term: 'Term 3', week: 'Week 2', topic: 'Number Systems - Binary, decimal, hexadecimal conversion', unit: 'Data Representation', strand: 'Strand 3: Programming' },
  { id: 'y13-w23', term: 'Term 3', week: 'Week 3', topic: 'Binary Arithmetic - Addition, subtraction, overflow', unit: 'Data Representation', strand: 'Strand 3: Programming' },
  { id: 'y13-w24', term: 'Term 3', week: 'Week 4', topic: 'Logic Gates and Truth Tables - AND, OR, NOT, XOR', unit: 'Data Representation', strand: 'Strand 3: Programming' },
  { id: 'y13-w25', term: 'Term 3', week: 'Week 5', topic: 'Programming Process - Problem analysis, design, coding, testing', unit: 'Programming II', strand: 'Strand 3: Programming' },
  { id: 'y13-w26', term: 'Term 3', week: 'Week 6', topic: 'Pseudocode and Flowcharts - Designing solutions before coding', unit: 'Programming II', strand: 'Strand 3: Programming' },
  { id: 'y13-w27', term: 'Term 3', week: 'Week 7', topic: 'Coding Practices - Variables, data types, operators, syntax', unit: 'Programming II', strand: 'Strand 3: Programming' },
  { id: 'y13-w28', term: 'Term 3', week: 'Week 8', topic: 'Modular Programming - Functions, procedures, code organization', unit: 'Programming II', strand: 'Strand 3: Programming' },
  { id: 'y13-w29', term: 'Term 3', week: 'Week 9', topic: 'Program Testing and Debugging - Finding and fixing errors', unit: 'Programming II', strand: 'Strand 3: Programming' },
  { id: 'y13-w30', term: 'Term 3', week: 'Week 10', topic: 'Term 3 Review - Data Representation and Programming', unit: 'Review', strand: 'Strand 3: Programming' },
  { id: 'y13-w31', term: 'Term 4', week: 'Week 1', topic: 'Microprocessor Components - CPU architecture, registers, memory', unit: 'Embedded Systems', strand: 'Strand 3: Programming' },
  { id: 'y13-w32', term: 'Term 4', week: 'Week 2', topic: 'Machine Code and Assembly - High-level to machine code', unit: 'Embedded Systems', strand: 'Strand 3: Programming' },
  { id: 'y13-w33', term: 'Term 4', week: 'Week 3', topic: 'Programming Microprocessors - Programs for embedded devices', unit: 'Embedded Systems', strand: 'Strand 3: Programming' },
  { id: 'y13-w34', term: 'Term 4', week: 'Week 4', topic: 'Embedded Devices in Daily Life - IoT, sensors, smart devices', unit: 'Embedded Systems', strand: 'Strand 3: Programming' },
  { id: 'y13-w35', term: 'Term 4', week: 'Week 5', topic: 'Cyber Security Threats - Viruses, malware, ransomware', unit: 'Safety & Security II', strand: 'Strand 4: ICT Issues' },
  { id: 'y13-w36', term: 'Term 4', week: 'Week 6', topic: 'Hackers and Online Identity - Identity theft, phishing', unit: 'Safety & Security II', strand: 'Strand 4: ICT Issues' },
  { id: 'y13-w37', term: 'Term 4', week: 'Week 7', topic: 'Cyber Security Guidelines - Best practices for protection', unit: 'Safety & Security II', strand: 'Strand 4: ICT Issues' },
  { id: 'y13-w38', term: 'Term 4', week: 'Week 8', topic: 'Ethical Issues in ICT - Copyright, plagiarism, privacy', unit: 'Ethics in ICT', strand: 'Strand 4: ICT Issues' },
  { id: 'y13-w39', term: 'Term 4', week: 'Week 9', topic: 'Sustainable ICT - E-waste, green computing, environmental impact', unit: 'Sustainability', strand: 'Strand 4: ICT Issues' },
  { id: 'y13-w40', term: 'Term 4', week: 'Week 10', topic: 'Term 4 Review - Embedded Systems and ICT Issues', unit: 'Review', strand: 'Strand 4: ICT Issues' },
];

const STATUS = { PENDING: 'pending', GENERATING: 'generating', SAVING: 'saving', DONE: 'done', ERROR: 'error', SKIPPED: 'skipped' };

export default function BatchGeneratorTab({ currentUser }) {
  const teacherSubjects = useMemo(() => getTeacherSubjects(currentUser?.department), [currentUser?.department]);

  const [selectedSubject, setSelectedSubject] = useState(teacherSubjects[0]?.key || 'ict');
  const [yearLevel, setYearLevel] = useState('');
  const [contentType, setContentType] = useState('both');
  const [assessmentType, setAssessmentType] = useState('worksheet');
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState([]);
  const cancelRef = useRef(false);

  // Get available year levels for selected subject
  const currentSubjectConfig = useMemo(() => {
    return teacherSubjects.find(s => s.key === selectedSubject) || teacherSubjects[0];
  }, [selectedSubject, teacherSubjects]);

  const availableYears = useMemo(() => {
    if (!currentSubjectConfig) return [];
    return currentSubjectConfig.yearLevels || [];
  }, [currentSubjectConfig]);

  // Set default year when subject changes
  React.useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(parseInt(yearLevel))) {
      setYearLevel(String(availableYears[0]));
    }
    setSelectedTopics(new Set());
    setProgress([]);
  }, [selectedSubject, availableYears]);

  // Get topics for current subject + year - fetched from API with retry
  const [topics, setTopics] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState(false);

  const fetchTopics = async (subject, yr) => {
    if (!yr || !subject) { setTopics([]); return; }
    setTopicsLoading(true);
    setTopicsError(false);
    const maxRetries = 3;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const tid = setTimeout(() => controller.abort(), 25000);
        const res = await fetch(`${API_URL}/api/curriculum-data/weekly-topics?subject_key=${subject}&year_level=${yr}`, { signal: controller.signal });
        clearTimeout(tid);
        if (res.ok) {
          const data = await res.json();
          setTopics(data);
          setTopicsLoading(false);
          return;
        }
        if (res.status >= 500 && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        setTopics([]);
        setTopicsLoading(false);
        return;
      } catch (e) {
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        setTopics([]);
        setTopicsError(true);
        setTopicsLoading(false);
        return;
      }
    }
  };

  useEffect(() => {
    fetchTopics(selectedSubject, parseInt(yearLevel));
  }, [selectedSubject, yearLevel]);

  const terms = useMemo(() => [...new Set(topics.map(t => t.term))], [topics]);

  const toggleTopic = (id) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedTopics(prev => prev.size === topics.length ? new Set() : new Set(topics.map(t => t.id)));
  };

  const selectTerm = (term) => {
    const tt = topics.filter(t => t.term === term);
    const allSel = tt.every(t => selectedTopics.has(t.id));
    setSelectedTopics(prev => {
      const next = new Set(prev);
      tt.forEach(t => allSel ? next.delete(t.id) : next.add(t.id));
      return next;
    });
  };

  const schoolType = useMemo(() => {
    const yr = parseInt(yearLevel);
    return yr >= 11 ? 'secondary' : yr >= 7 ? 'junior_secondary' : 'primary';
  }, [yearLevel]);

  const generateNotes = async (topic, yr) => {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 90000);
    const res = await fetch(`${API_URL}/api/ai-lessons/generate-notes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: ctrl.signal,
      body: JSON.stringify({ topic: topic.topic, subject: selectedSubject, year_level: yr, school_type: schoolType, language: 'en', detail_level: 'detailed' })
    });
    clearTimeout(tid);
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  };

  const saveNotes = async (data, topic, yr) => {
    const n = data.notes;
    const res = await fetch(`${API_URL}/api/ai-lessons/save-lesson?teacher_id=${currentUser.id}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: n.title || `Y${yr} ${currentSubjectConfig?.label || selectedSubject} ${topic.week}: ${topic.topic.split(' - ')[0]}`, subject: selectedSubject, year_level: yr, term: topic.term, topic: topic.topic.split(' - ')[0], content: JSON.stringify(n), is_week_plan: false })
    });
    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    return res.json();
  };

  const generateAssessment = async (topic, yr, aType) => {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 90000);
    const res = await fetch(`${API_URL}/api/ai-lessons/generate-assessment`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: ctrl.signal,
      body: JSON.stringify({ topic: topic.topic, subject: selectedSubject, year_level: yr, assessment_type: aType, difficulty: 'medium', num_questions: 15, question_types: ['multiple_choice', 'true_false', 'fill_blank', 'short_answer'], school_type: schoolType, language: 'en', include_answer_key: true })
    });
    clearTimeout(tid);
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  };

  const saveAssessment = async (data, topic, yr, aType) => {
    const a = data.assessment;
    const res = await fetch(`${API_URL}/api/ai-lessons/save-assessment?teacher_id=${currentUser.id}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: a.title || `Y${yr} ${currentSubjectConfig?.label || selectedSubject} ${aType}: ${topic.topic.split(' - ')[0]}`, subject: selectedSubject, year_level: yr, term: topic.term, topic: topic.topic.split(' - ')[0], assessment_type: aType, difficulty: 'medium', questions: a.questions || [], answer_key: a.answer_key || [], total_marks: a.total_marks || 15, time_limit: a.time_limit || 30, instructions: a.instructions || '' })
    });
    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    return res.json();
  };

  const generateLessonPlan = async (topic, yr) => {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 90000);
    const res = await fetch(`${API_URL}/api/ai-lessons/generate`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: ctrl.signal,
      body: JSON.stringify({ topic: topic.topic, subject: selectedSubject, year_level: yr, duration: 45, school_type: schoolType, language: 'en', teaching_style: 'interactive', student_abilities: 'mixed' })
    });
    clearTimeout(tid);
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  };

  const saveLessonPlan = async (data, topic, yr) => {
    const l = data.lesson;
    const res = await fetch(`${API_URL}/api/ai-lessons/save-lesson?teacher_id=${currentUser.id}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: l.title || `Y${yr} ${currentSubjectConfig?.label || selectedSubject} ${topic.week}: ${topic.topic.split(' - ')[0]}`, subject: selectedSubject, year_level: yr, term: topic.term, topic: topic.topic.split(' - ')[0], duration: 45, objectives: l.objectives || [], introduction: l.introduction || '', activities: l.activities || [], materials: l.materials || '', assessment: l.assessment || '', homework: l.homework || '', is_week_plan: false })
    });
    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    return res.json();
  };

  const generateSchemeForTerm = async (termNum, numWeeks, yr) => {
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 90000);
    const res = await fetch(`${API_URL}/api/ai-lessons/generate-scheme`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: ctrl.signal,
      body: JSON.stringify({ subject: selectedSubject, year_level: yr, term: termNum, num_weeks: numWeeks, school_type: schoolType, language: 'en' })
    });
    clearTimeout(tid);
    if (!res.ok) throw new Error(`Failed: ${res.status}`);
    return res.json();
  };

  const saveSchemeForTerm = async (data, termNum, yr) => {
    const weeks = data.weeks;
    const subLabel = currentSubjectConfig?.label || selectedSubject;
    const res = await fetch(`${API_URL}/api/ai-lessons/save-lesson?teacher_id=${currentUser.id}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: `Scheme of Work: Y${yr} ${subLabel} - Term ${termNum}`, subject: selectedSubject, year_level: yr, term: `Term ${termNum}`, topic: `Term ${termNum} - ${weeks.length} weeks`, is_week_plan: true, week_lessons: weeks })
    });
    if (!res.ok) throw new Error(`Save failed: ${res.status}`);
    return res.json();
  };

  const updateProgress = (topicId, type, status, title) => {
    setProgress(prev => {
      const idx = prev.findIndex(p => p.topicId === topicId && p.type === type);
      if (idx >= 0) { const next = [...prev]; next[idx] = { ...next[idx], status, title: title || next[idx].title }; return next; }
      return [...prev, { topicId, type, status, title: title || '' }];
    });
  };

  const startBatch = async () => {
    if (selectedTopics.size === 0) { toast.error('Select at least one week'); return; }
    cancelRef.current = false;
    setIsRunning(true);
    setProgress([]);

    const yr = parseInt(yearLevel);
    const selected = topics.filter(t => selectedTopics.has(t.id));
    const aType = assessmentType;
    const aLabel = ASSESSMENT_TYPES.find(a => a.value === aType)?.label || aType;

    const incNotes = ['notes', 'both', 'notes_lesson', 'all'].includes(contentType);
    const incAssess = ['assessment', 'both', 'all'].includes(contentType);
    const incLesson = ['lesson_plan', 'notes_lesson', 'all'].includes(contentType);
    const incScheme = contentType === 'scheme';

    const tasks = [];

    if (incScheme) {
      // Group selected topics by term, generate one scheme per term
      const termMap = {};
      for (const topic of selected) {
        if (!termMap[topic.term]) termMap[topic.term] = [];
        termMap[topic.term].push(topic);
      }
      for (const [termLabel, termTopics] of Object.entries(termMap)) {
        const termNum = parseInt(termLabel.replace('Term ', ''));
        const totalWeeksInTerm = topics.filter(t => t.term === termLabel).length;
        tasks.push({ topic: termTopics[0], type: 'scheme', termLabel, termNum, numWeeks: totalWeeksInTerm || 10 });
      }
    } else {
      for (const topic of selected) {
        if (incNotes) tasks.push({ topic, type: 'notes' });
        if (incAssess) tasks.push({ topic, type: 'assessment' });
        if (incLesson) tasks.push({ topic, type: 'lesson_plan' });
      }
    }

    tasks.forEach(t => {
      const title = t.type === 'scheme'
        ? `Scheme: ${t.termLabel}`
        : `${t.topic.week}: ${t.topic.topic.split(' - ')[0]}`;
      const taskId = t.type === 'scheme' ? `scheme-${t.termLabel}` : t.topic.id;
      updateProgress(taskId, t.type, STATUS.PENDING, title);
    });

    let completed = 0, failed = 0;
    for (const task of tasks) {
      if (cancelRef.current) {
        const taskId = task.type === 'scheme' ? `scheme-${task.termLabel}` : task.topic.id;
        updateProgress(taskId, task.type, STATUS.SKIPPED);
        continue;
      }
      try {
        if (task.type === 'notes') {
          updateProgress(task.topic.id, 'notes', STATUS.GENERATING);
          const r = await generateNotes(task.topic, yr);
          if (cancelRef.current) { updateProgress(task.topic.id, 'notes', STATUS.SKIPPED); continue; }
          updateProgress(task.topic.id, 'notes', STATUS.SAVING);
          await saveNotes(r, task.topic, yr);
          updateProgress(task.topic.id, 'notes', STATUS.DONE);
          completed++;
        } else if (task.type === 'assessment') {
          updateProgress(task.topic.id, 'assessment', STATUS.GENERATING, `${aLabel} - ${task.topic.week}`);
          const r = await generateAssessment(task.topic, yr, aType);
          if (cancelRef.current) { updateProgress(task.topic.id, 'assessment', STATUS.SKIPPED); continue; }
          updateProgress(task.topic.id, 'assessment', STATUS.SAVING);
          await saveAssessment(r, task.topic, yr, aType);
          updateProgress(task.topic.id, 'assessment', STATUS.DONE);
          completed++;
        } else if (task.type === 'lesson_plan') {
          updateProgress(task.topic.id, 'lesson_plan', STATUS.GENERATING, `Lesson Plan - ${task.topic.week}`);
          const r = await generateLessonPlan(task.topic, yr);
          if (cancelRef.current) { updateProgress(task.topic.id, 'lesson_plan', STATUS.SKIPPED); continue; }
          updateProgress(task.topic.id, 'lesson_plan', STATUS.SAVING);
          await saveLessonPlan(r, task.topic, yr);
          updateProgress(task.topic.id, 'lesson_plan', STATUS.DONE);
          completed++;
        } else if (task.type === 'scheme') {
          const schemeId = `scheme-${task.termLabel}`;
          updateProgress(schemeId, 'scheme', STATUS.GENERATING, `Scheme: ${task.termLabel}`);
          const r = await generateSchemeForTerm(task.termNum, task.numWeeks, yr);
          if (cancelRef.current) { updateProgress(schemeId, 'scheme', STATUS.SKIPPED); continue; }
          updateProgress(schemeId, 'scheme', STATUS.SAVING);
          await saveSchemeForTerm(r, task.termNum, yr);
          updateProgress(schemeId, 'scheme', STATUS.DONE);
          completed++;
        }
      } catch (err) {
        const taskId = task.type === 'scheme' ? `scheme-${task.termLabel}` : task.topic.id;
        console.error(`Failed: ${task.type} for ${task.type === 'scheme' ? task.termLabel : task.topic.topic}`, err);
        updateProgress(taskId, task.type, STATUS.ERROR);
        failed++;
      }
    }

    setIsRunning(false);
    if (cancelRef.current) toast.info(`Stopped. ${completed} done, ${failed} failed.`);
    else if (failed > 0) toast.warning(`Done: ${completed} generated, ${failed} failed.`);
    else toast.success(`All ${completed} items saved to My Library!`);
  };

  const stopBatch = () => { cancelRef.current = true; toast.info('Stopping...'); };

  const incNotes = ['notes', 'both', 'notes_lesson', 'all'].includes(contentType);
  const incAssess = ['assessment', 'both', 'all'].includes(contentType);
  const incLesson = ['lesson_plan', 'notes_lesson', 'all'].includes(contentType);
  const incScheme = contentType === 'scheme';

  // Calculate total tasks
  const totalTasks = incScheme
    ? [...new Set(topics.filter(t => selectedTopics.has(t.id)).map(t => t.term))].length
    : selectedTopics.size * ((incNotes ? 1 : 0) + (incAssess ? 1 : 0) + (incLesson ? 1 : 0));
  const doneTasks = progress.filter(p => p.status === STATUS.DONE).length;
  const currentTask = progress.find(p => p.status === STATUS.GENERATING || p.status === STATUS.SAVING);
  const aLabel = ASSESSMENT_TYPES.find(a => a.value === assessmentType)?.label || assessmentType;

  const contentLabel = () => {
    if (incScheme) return 'Scheme of Work';
    const parts = [];
    if (incNotes) parts.push('Notes');
    if (incAssess) parts.push(aLabel);
    if (incLesson) parts.push('Lesson Plan');
    return parts.join(' + ');
  };

  return (
    <div className="space-y-6">
      {/* Config */}
      <Card className="border-slate-700 bg-slate-800/50">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            Batch Generate Content
          </CardTitle>
          <p className="text-sm text-slate-400">Select your subject, year level, and weeks to generate Notes, Assessments, Lesson Plans, or Schemes of Work. Auto-saved to My Library.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* Subject Selector */}
            <div>
              <Label className="text-slate-300 mb-1.5 block">Subject</Label>
              <Select value={selectedSubject} onValueChange={v => { setSelectedSubject(v); setSelectedTopics(new Set()); setProgress([]); }} disabled={isRunning}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="batch-subject-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {teacherSubjects.map(s => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Year Level */}
            <div>
              <Label className="text-slate-300 mb-1.5 block">Year Level</Label>
              <Select value={yearLevel} onValueChange={v => { setYearLevel(v); setSelectedTopics(new Set()); setProgress([]); }} disabled={isRunning}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="batch-year-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {availableYears.map(y => (
                    <SelectItem key={y} value={String(y)}>Year {y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Content Type */}
            <div>
              <Label className="text-slate-300 mb-1.5 block">Content</Label>
              <Select value={contentType} onValueChange={setContentType} disabled={isRunning}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="batch-content-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="notes">Notes only</SelectItem>
                  <SelectItem value="assessment">{aLabel} only</SelectItem>
                  <SelectItem value="lesson_plan">Lesson Plan only</SelectItem>
                  <SelectItem value="scheme">Scheme of Work (per term)</SelectItem>
                  <SelectItem value="both">Notes + {aLabel}</SelectItem>
                  <SelectItem value="notes_lesson">Notes + Lesson Plan</SelectItem>
                  <SelectItem value="all">Notes + {aLabel} + Lesson Plan</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {incAssess && (
            <div>
              <Label className="text-slate-300 mb-1.5 block">Assessment Type</Label>
              <Select value={assessmentType} onValueChange={setAssessmentType} disabled={isRunning}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="batch-assessment-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSESSMENT_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {incScheme && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-xs text-amber-300">
                <strong>Scheme of Work:</strong> One scheme will be generated per term. Select topics from the terms you want schemes for — the full term will be covered.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Weekly Topics */}
      {topics.length > 0 ? (
        <Card className="border-slate-700 bg-slate-800/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base text-white">Select Weeks ({selectedTopics.size} of {topics.length})</CardTitle>
              <Button variant="ghost" size="sm" onClick={selectAll} disabled={isRunning} className="text-slate-300 hover:text-white" data-testid="batch-select-all">
                {selectedTopics.size === topics.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {terms.map(term => {
              const tt = topics.filter(t => t.term === term);
              const allSel = tt.every(t => selectedTopics.has(t.id));
              const selCount = tt.filter(t => selectedTopics.has(t.id)).length;
              return (
                <div key={term}>
                  <button onClick={() => selectTerm(term)} disabled={isRunning}
                    className="flex items-center gap-2 mb-2 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors"
                    data-testid={`batch-term-${term.replace(/\s/g,'-').toLowerCase()}`}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${allSel ? 'bg-amber-500 border-amber-500 text-black' : 'border-slate-500'}`}>
                      {allSel && '\u2713'}
                    </div>
                    {term} <span className="text-slate-500 font-normal">({selCount}/{tt.length} weeks)</span>
                  </button>
                  <div className="grid gap-1 pl-2">
                    {tt.map(topic => {
                      const sel = selectedTopics.has(topic.id);
                      const noteP = progress.find(p => p.topicId === topic.id && p.type === 'notes');
                      const assessP = progress.find(p => p.topicId === topic.id && p.type === 'assessment');
                      const lessonP = progress.find(p => p.topicId === topic.id && p.type === 'lesson_plan');
                      const schemeP = progress.find(p => p.topicId === `scheme-${topic.term}` && p.type === 'scheme');
                      return (
                        <div key={topic.id}
                          className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-all ${sel ? 'bg-slate-700/80 ring-1 ring-amber-500/30' : 'bg-slate-800/30 hover:bg-slate-700/40'} ${isRunning ? 'cursor-default' : ''}`}
                          onClick={() => !isRunning && toggleTopic(topic.id)}
                          data-testid={`batch-topic-${topic.id}`}>
                          <div className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center text-xs ${sel ? 'bg-amber-500 border-amber-500 text-black' : 'border-slate-500'}`}>
                            {sel && '\u2713'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-amber-400/80 w-14 flex-shrink-0">{topic.week}</span>
                              <p className="text-sm text-white leading-snug">{topic.topic}</p>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5 ml-16">{topic.unit || ''}{topic.strand ? ` \u00b7 ${topic.strand}` : ''}</p>
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0">
                            {noteP && <StatusIcon status={noteP.status} type="notes" />}
                            {assessP && <StatusIcon status={assessP.status} type="assessment" />}
                            {lessonP && <StatusIcon status={lessonP.status} type="lesson_plan" />}
                            {schemeP && <StatusIcon status={schemeP.status} type="scheme" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <Separator className="bg-slate-700/50 mt-3" />
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : yearLevel ? (
        <Card className="border-slate-700 bg-slate-800/50">
          <CardContent className="py-12 text-center">
            {topicsError ? (
              <>
                <p className="text-amber-400">Failed to load curriculum data — database may be waking up.</p>
                <Button onClick={() => fetchTopics(selectedSubject, parseInt(yearLevel))} variant="outline" size="sm" className="mt-3" data-testid="retry-topics-btn">
                  Retry
                </Button>
              </>
            ) : (
              <>
                <p className="text-slate-400">No weekly curriculum data available for Year {yearLevel} {currentSubjectConfig?.label || selectedSubject} yet.</p>
                <p className="text-sm text-slate-500 mt-2">Use the Notes or Assessment tab to generate content manually for this subject.</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : null}

      {/* Action Bar */}
      {topics.length > 0 && (
        <Card className="border-slate-700 bg-slate-800/50 sticky bottom-4">
          <CardContent className="p-4">
            {isRunning && (
              <div className="mb-3">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-slate-300 truncate mr-4">
                    {currentTask ? `${currentTask.type === 'notes' ? 'Notes' : currentTask.type === 'lesson_plan' ? 'Lesson Plan' : currentTask.type === 'scheme' ? 'Scheme' : aLabel}: ${currentTask.title}` : 'Finishing...'}
                  </span>
                  <span className="text-amber-400 font-medium flex-shrink-0">{doneTasks} / {totalTasks}</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div className="bg-gradient-to-r from-amber-500 to-amber-400 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${totalTasks > 0 ? (doneTasks / totalTasks) * 100 : 0}%` }} />
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-400">
                {selectedTopics.size > 0
                  ? incScheme
                    ? `${totalTasks} scheme${totalTasks !== 1 ? 's' : ''} (${totalTasks} term${totalTasks !== 1 ? 's' : ''} selected)`
                    : `${totalTasks} item${totalTasks !== 1 ? 's' : ''} (${selectedTopics.size} week${selectedTopics.size !== 1 ? 's' : ''} \u00d7 ${contentLabel()})`
                  : 'Select weeks above to begin'}
              </p>
              {isRunning ? (
                <Button onClick={stopBatch} variant="destructive" size="sm" data-testid="batch-stop-btn"><Square className="w-4 h-4 mr-1.5" /> Stop</Button>
              ) : (
                <Button onClick={startBatch} disabled={selectedTopics.size === 0} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="batch-start-btn">
                  <Play className="w-4 h-4 mr-1.5" /> Generate
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusIcon({ status, type }) {
  const iconMap = { notes: FileText, assessment: FileQuestion, lesson_plan: BookOpen, scheme: LayoutList };
  const Icon = iconMap[type] || FileText;
  const c = {
    [STATUS.PENDING]: { color: 'text-slate-500', bg: 'bg-slate-700' },
    [STATUS.GENERATING]: { color: 'text-amber-400', bg: 'bg-amber-500/10', spin: true },
    [STATUS.SAVING]: { color: 'text-blue-400', bg: 'bg-blue-500/10', spin: true },
    [STATUS.DONE]: { color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    [STATUS.ERROR]: { color: 'text-red-400', bg: 'bg-red-500/10' },
    [STATUS.SKIPPED]: { color: 'text-slate-500', bg: 'bg-slate-700' },
  }[status] || { color: 'text-slate-500', bg: 'bg-slate-700' };
  const typeLabels = { notes: 'Notes', assessment: 'Assessment', lesson_plan: 'Lesson Plan', scheme: 'Scheme' };
  return (
    <div className={`w-6 h-6 rounded flex items-center justify-center ${c.bg}`} title={`${typeLabels[type] || type}: ${status}`}>
      {status === STATUS.DONE ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
       : status === STATUS.ERROR ? <XCircle className="w-3.5 h-3.5 text-red-400" />
       : c.spin ? <Loader2 className={`w-3.5 h-3.5 ${c.color} animate-spin`} />
       : <Icon className={`w-3.5 h-3.5 ${c.color}`} />}
    </div>
  );
}
